import { Typography, CircularProgress, Grid, Chip } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import React, { useEffect } from "react";
import { useParams } from "react-router";
import { Wrapper } from "components/wrapper";
import { DetailCard, DetailField, DetailSectionTitle } from "components/detailPage";
import { DocumentList } from "components/documentList";
import { ExportButtons } from "components/exportButtons";
import { useLicitacaoDetailsState } from "./context/licitacao-details-context";
import { useLicitacaoDetailsController } from "./context/licitacao-details-controller";

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '-';

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';

export function LicitacaoDetailsPage() {
  const { licitacaoDetails, loading, error } = useLicitacaoDetailsState();
  const { loadLicitacaoDetails, clearLicitacaoDetails, exportLicitacaoDetail } = useLicitacaoDetailsController();
  let { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (id) {
      loadLicitacaoDetails(parseInt(id));
    }

    return () => {
      clearLicitacaoDetails();
    };
  }, [id, loadLicitacaoDetails, clearLicitacaoDetails]);

  if (loading) {
    return (
      <Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Typography color="error" variant="h6">{error}</Typography>
      </Wrapper>
    );
  }

  if (!licitacaoDetails) {
    return (
      <Wrapper>
        <Typography variant="h6">Licitação não encontrada.</Typography>
      </Wrapper>
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Licitações", href: "/licitacoes" },
          { label: licitacaoDetails.numLicitacao },
        ]}
      />
      <PageTitle>LICITAÇÃO {licitacaoDetails.numLicitacao}</PageTitle>
      <Wrapper>
        <ExportButtons onExport={(format) => exportLicitacaoDetail(Number(id), format)} />
        <DetailCard>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Chip label={licitacaoDetails.situacaoName} color="primary" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DetailField label="Órgão" value={licitacaoDetails.orgaoName} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailField label="Modalidade" value={licitacaoDetails.modalidadeName} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Fase" value={licitacaoDetails.faseName} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Nº Processo Administrativo" value={licitacaoDetails.numProcessoAdministrativo} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Nº Edital" value={licitacaoDetails.numEdital} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Abertura e Valores</DetailSectionTitle>
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Data/Hora de Abertura" value={formatDate(licitacaoDetails.dtaHoraAbertura)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Local de Abertura" value={licitacaoDetails.localAberturaLicitacao} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Registro de Preço" value={licitacaoDetails.indicativoRegistroPreco ? 'Sim' : 'Não'} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DetailField
                label="Valor Estimado"
                value={licitacaoDetails.valorEstimado != null ? formatCurrency(licitacaoDetails.valorEstimado) : (licitacaoDetails.textoJustificativaValorEstimado || '-')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailField label="Valor Homologado" value={formatCurrency(licitacaoDetails.valorHomologado)} />
            </Grid>

            {licitacaoDetails.dtaHomologacao && (
              <Grid item xs={12} sm={6}>
                <DetailField label="Data de Homologação" value={formatDate(licitacaoDetails.dtaHomologacao)} />
              </Grid>
            )}

            <Grid item xs={12}>
              <DetailSectionTitle>Detalhes</DetailSectionTitle>
            </Grid>

            <Grid item xs={12}>
              <DetailField label="Objeto" value={licitacaoDetails.textoObjetoLicitacao} />
            </Grid>

            {licitacaoDetails.pncpUrl && (
              <Grid item xs={12}>
                <DetailField
                  label="PNCP"
                  value={<a href={licitacaoDetails.pncpUrl} target="_blank" rel="noreferrer">{licitacaoDetails.pncpUrl}</a>}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <DetailSectionTitle>Documentos</DetailSectionTitle>
              <DocumentList documents={licitacaoDetails.documentos} />
            </Grid>
          </Grid>
        </DetailCard>
      </Wrapper>
    </>
  );
}
